import React, { useCallback, useEffect, useState } from 'react';
import './UpcomingEvents.css';
import { EventSection } from '../data/upcomingEvents';
import EventCalendar from './EventCalendar';
import EventFormModal from './EventFormModal';
import ConfirmModal from './ConfirmModal';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

function eventsForDate(date: string, events: EventSection[]): EventSection[] {
    return events.filter((e) => {
        if (e.endDate) {
            return date >= e.date && date <= e.endDate;
        }
        return e.date === date;
    });
}

function formatSelectedDate(iso: string): string {
    const [year, month, day] = iso.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function rowToEvent(row: Record<string, unknown>): EventSection {
    return {
        id: row.id as string,
        title: row.title as string,
        date: row.date as string,
        endDate: (row.end_date as string | null) ?? undefined,
        time: (row.time as string | null) ?? undefined,
        location: (row.location as string | null) ?? undefined,
        description: (row.description as string | null) ?? undefined,
        archivedAt: (row.archived_at as string | null) ?? undefined,
    };
}

const UpcomingEvents: React.FC = () => {
    const { user } = useAuth();
    const [events, setEvents] = useState<EventSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState<EventSection | null>(null);
    const [pendingArchiveId, setPendingArchiveId] = useState<string | null>(null);
    const [pendingPermDeleteId, setPendingPermDeleteId] = useState<string | null>(null);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        const { data, error } = await supabase
            .from('events')
            .select('*')
            .order('date', { ascending: true });
        if (error) {
            setFetchError('Failed to load events. Please try again later.');
        } else {
            setEvents((data as Record<string, unknown>[]).map(rowToEvent));
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleArchive = async (id: string) => {
        setActionError(null);
        const archivedAt = new Date().toISOString();
        const { error } = await supabase
            .from('events')
            .update({ archived_at: archivedAt })
            .eq('id', id);
        if (error) {
            console.error('Archive failed:', error);
            setActionError(`Failed to archive event: ${error.message}`);
            setPendingArchiveId(null);
            return;
        }
        setEvents((prev) => prev.map((e) => e.id === id ? { ...e, archivedAt } : e));
        setPendingArchiveId(null);
    };

    const handleRestore = async (id: string) => {
        setActionError(null);
        const { error } = await supabase
            .from('events')
            .update({ archived_at: null })
            .eq('id', id);
        if (error) {
            console.error('Restore failed:', error);
            setActionError(`Failed to restore event: ${error.message}`);
            return;
        }
        setEvents((prev) => prev.map((e) => e.id === id ? { ...e, archivedAt: undefined } : e));
    };

    const handlePermanentDelete = async (id: string) => {
        setActionError(null);
        const { error } = await supabase.from('events').delete().eq('id', id);
        if (error) {
            console.error('Delete failed:', error);
            setActionError(`Failed to delete event: ${error.message}`);
            setPendingPermDeleteId(null);
            return;
        }
        setEvents((prev) => prev.filter((e) => e.id !== id));
        setPendingPermDeleteId(null);
    };

    const activeEvents = events.filter((e) => !e.archivedAt);
    const archivedEvents = events.filter((e) => !!e.archivedAt);

    const displayedEvents = selectedDate
        ? eventsForDate(selectedDate, activeEvents)
        : activeEvents;

    const pendingArchiveEvent = pendingArchiveId ? events.find((e) => e.id === pendingArchiveId) : null;
    const pendingPermDeleteEvent = pendingPermDeleteId ? events.find((e) => e.id === pendingPermDeleteId) : null;

    return (
        <section className="upcoming-events-section">
            <div className="container">
                <h1 className="page-title">Upcoming Events</h1>
                <p className="page-subtitle">
                    Keep up to date with the latest gradings, competitions, and club social events
                </p>

                <EventCalendar
                    events={activeEvents}
                    selectedDate={selectedDate}
                    onSelectDate={setSelectedDate}
                />

                <div className="events-list-header">
                    <div className="events-list-heading">
                        {selectedDate ? (
                            <>
                                <h2 className="events-list-title">
                                    Events on {formatSelectedDate(selectedDate)}
                                </h2>
                                <button
                                    className="clear-date-btn"
                                    onClick={() => setSelectedDate(null)}
                                    aria-label="Show all events"
                                >
                                    Show all events
                                </button>
                            </>
                        ) : (
                            <h2 className="events-list-title">All Upcoming Events</h2>
                        )}
                        {user && (
                            <button
                                className="admin-add-btn"
                                onClick={() => setShowAddModal(true)}
                                aria-label="Add event"
                            >
                                + Add Event
                            </button>
                        )}
                    </div>
                </div>

                {loading && <p className="no-events-message">Loading events…</p>}
                {fetchError && <p className="no-events-message">{fetchError}</p>}
                {actionError && (
                    <p className="no-events-message" role="alert">{actionError}</p>
                )}

                {!loading && !fetchError && (
                    <div className="club-content">
                        {displayedEvents.length > 0 ? (
                            displayedEvents.map((event) => (
                                <div key={event.id} className="content-section">
                                    <div className="event-card-header">
                                        <h2 id={event.id}>{event.title}</h2>
                                        {user && (
                                            <div className="admin-event-actions">
                                                <button
                                                    className="admin-edit-btn"
                                                    onClick={() => setEditingEvent(event)}
                                                    aria-label={`Edit ${event.title}`}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="admin-archive-btn"
                                                    onClick={() => setPendingArchiveId(event.id)}
                                                    aria-label={`Archive ${event.title}`}
                                                >
                                                    Archive
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="event-details">
                                        {event.date && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Date:</span>
                                                {event.endDate
                                                    ? `${formatSelectedDate(event.date)} – ${formatSelectedDate(event.endDate)}`
                                                    : formatSelectedDate(event.date)}
                                            </p>
                                        )}
                                        {event.time && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Time:</span>
                                                {event.time}
                                            </p>
                                        )}
                                        {event.location && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Location:</span>
                                                {event.location}
                                            </p>
                                        )}
                                        {event.description && (
                                            <p className="event-description">{event.description}</p>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="no-events-message">No events on this day.</p>
                        )}
                    </div>
                )}

                {user && !loading && !fetchError && archivedEvents.length > 0 && (
                    <>
                        <div className="archived-section-heading">
                            <span>Archived Events</span>
                        </div>
                        <div className="club-content">
                            {archivedEvents.map((event) => (
                                <div key={event.id} className="content-section content-section--archived">
                                    <div className="event-card-header">
                                        <h2 id={event.id}>{event.title}</h2>
                                        <div className="admin-event-actions">
                                            <button
                                                className="admin-restore-btn"
                                                onClick={() => handleRestore(event.id)}
                                                aria-label={`Restore ${event.title}`}
                                            >
                                                Restore
                                            </button>
                                            <button
                                                className="admin-permdelete-btn"
                                                onClick={() => setPendingPermDeleteId(event.id)}
                                                aria-label={`Permanently delete ${event.title}`}
                                            >
                                                Delete Forever
                                            </button>
                                        </div>
                                    </div>
                                    <div className="event-details">
                                        {event.date && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Date:</span>
                                                {event.endDate
                                                    ? `${formatSelectedDate(event.date)} – ${formatSelectedDate(event.endDate)}`
                                                    : formatSelectedDate(event.date)}
                                            </p>
                                        )}
                                        {event.time && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Time:</span>
                                                {event.time}
                                            </p>
                                        )}
                                        {event.location && (
                                            <p className="event-detail-row">
                                                <span className="event-detail-label">Location:</span>
                                                {event.location}
                                            </p>
                                        )}
                                        {event.description && (
                                            <p className="event-description">{event.description}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {showAddModal && (
                <EventFormModal
                    mode="add"
                    onClose={() => setShowAddModal(false)}
                    onSaved={fetchEvents}
                />
            )}
            {editingEvent && (
                <EventFormModal
                    mode="edit"
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onSaved={fetchEvents}
                />
            )}
            {pendingArchiveEvent && (
                <ConfirmModal
                    title="Archive this event?"
                    eventName={pendingArchiveEvent.title}
                    message="will be hidden from the site but can be restored at any time."
                    confirmLabel="Archive"
                    variant="warning"
                    onConfirm={() => handleArchive(pendingArchiveEvent.id)}
                    onCancel={() => setPendingArchiveId(null)}
                />
            )}
            {pendingPermDeleteEvent && (
                <ConfirmModal
                    title="Permanently delete this event?"
                    eventName={pendingPermDeleteEvent.title}
                    message="will be removed from the database. This cannot be undone."
                    confirmLabel="Delete Forever"
                    variant="danger"
                    onConfirm={() => handlePermanentDelete(pendingPermDeleteEvent.id)}
                    onCancel={() => setPendingPermDeleteId(null)}
                />
            )}
        </section>
    );
};

export default UpcomingEvents;
